import type { ImportPurchaseHistoryPayload } from './domain';

type Purchases = ImportPurchaseHistoryPayload['purchases'];

const COLUMNS: Record<string, string> = {
  datum: 'date', geschaeft: 'chain', 'geschäft': 'chain', filiale: 'branch', adresse: 'address', ort: 'city', plz: 'postalCode',
  produkt: 'product', marke: 'brand', ean: 'ean', menge: 'quantity', einheit: 'unit', einzelpreis: 'unitPrice', gesamtpreis: 'totalPrice',
};

function splitLine(line: string, sep: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (quoted) {
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; }
      else if (c === '"') quoted = false;
      else cur += c;
    } else if (c === '"') quoted = true;
    else if (c === sep) { out.push(cur.trim()); cur = ''; }
    else cur += c;
  }
  out.push(cur.trim());
  return out;
}

function num(raw: string | undefined, label: string, row: number): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw.replace(/\s|€/g, '').replace(',', '.'));
  if (!Number.isFinite(n)) throw new Error(`Zeile ${row}: ungültiger Wert für ${label}: "${raw}"`);
  return n;
}

/** Datum als TT.MM.JJJJ oder ISO (JJJJ-MM-TT[THH:MM]). */
function date(raw: string, row: number): string {
  const m = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(raw);
  const iso = m ? `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}T12:00:00Z` : raw;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) throw new Error(`Zeile ${row}: ungültiges Datum "${raw}"`);
  return d.toISOString();
}

export function parseHistoryCsv(text: string): Purchases {
  const lines = text.replace(/^﻿/, '').split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) throw new Error('CSV benötigt eine Kopfzeile und mindestens eine Datenzeile.');
  const sep = lines[0].split(';').length >= lines[0].split(',').length ? ';' : ',';
  const header = splitLine(lines[0], sep).map((h) => COLUMNS[h.toLowerCase()]);
  for (const required of ['date', 'chain', 'product', 'quantity']) {
    if (!header.includes(required)) throw new Error(`Spalte fehlt: ${required}`);
  }

  const groups = new Map<string, Purchases[number]>();
  lines.slice(1).forEach((line, idx) => {
    const row = idx + 2;
    const cells = splitLine(line, sep);
    const r: Record<string, string> = {};
    header.forEach((k, i) => { if (k) r[k] = cells[i] ?? ''; });
    if (!r.date || !r.chain || !r.product) throw new Error(`Zeile ${row}: Datum, Geschäft und Produkt sind erforderlich.`);
    const quantity = num(r.quantity, 'Menge', row);
    const unitPrice = num(r.unitPrice, 'Einzelpreis', row);
    const totalPrice = num(r.totalPrice, 'Gesamtpreis', row);
    if (!quantity || quantity <= 0) throw new Error(`Zeile ${row}: Menge muss größer als 0 sein.`);
    if (unitPrice == null && totalPrice == null) throw new Error(`Zeile ${row}: Einzel- oder Gesamtpreis erforderlich.`);

    const purchasedAt = date(r.date, row);
    const key = [purchasedAt, r.chain, r.branch, r.address].join('|').toLowerCase();
    let purchase = groups.get(key);
    if (!purchase) {
      purchase = {
        purchasedAt,
        store: { chainName: r.chain, branchName: r.branch || undefined, address: r.address || undefined, city: r.city || undefined, postalCode: r.postalCode || undefined },
        items: [],
      };
      groups.set(key, purchase);
    }
    purchase.items.push({
      productName: r.product, brand: r.brand || undefined, ean: r.ean || undefined,
      quantity, unit: (r.unit || 'STUECK').toUpperCase(), unitPrice, totalPrice,
    });
  });
  return [...groups.values()];
}
